import * as React from "react"
import { Eye, EyeOff, X } from "lucide-react"

import { cn } from "@/shadcn/lib/utils"
import { Icon, type IconProps } from "@/shared/components/Icon"
import type { LucideIcon } from "lucide-react"

type InputSideRenderContext = {
  disabled?: boolean
  readOnly?: boolean
  hasValue: boolean
  type?: React.ComponentProps<"input">["type"]
  effectiveType?: React.ComponentProps<"input">["type"]
  isPasswordVisible: boolean
  hasBuiltInContent: boolean
}

type InputProps = React.ComponentProps<"input"> & {
  inputClassName?: string
  iconLeft?: LucideIcon
  iconRight?: LucideIcon
  iconLeftProps?: Omit<IconProps, "icon" | "data-icon">
  iconRightProps?: Omit<IconProps, "icon" | "data-icon">
  iconProps?: Omit<IconProps, "icon" | "data-icon">
  clearable?: boolean
  clearLabel?: string
  renderLeftContent?: (context: InputSideRenderContext) => React.ReactNode
  renderRightContent?: (context: InputSideRenderContext) => React.ReactNode
}

function Input({
  className,
  inputClassName,
  type,
  iconLeft,
  iconRight,
  iconLeftProps,
  iconRightProps,
  iconProps,
  clearable = true,
  clearLabel = "Clear input",
  renderLeftContent,
  renderRightContent,
  disabled,
  readOnly,
  onChange,
  value,
  ref: forwardedRef,
  "aria-invalid": ariaInvalid,
  ...props
}: InputProps) {
  const ref = React.useRef<HTMLInputElement>(null)
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false)

  const setInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      ref.current = node

      if (typeof forwardedRef === "function") {
        forwardedRef(node)
        return
      }

      if (forwardedRef) {
        forwardedRef.current = node
      }
    },
    [forwardedRef]
  )

  const hasValue = Boolean(value?.toString().trim().length)
  const isClearable = clearable && typeof onChange === "function" && !readOnly
  const isPasswordInput = type === "password"
  const shouldRenderPasswordToggle = isPasswordInput && hasValue
  const effectiveType = isPasswordInput && isPasswordVisible ? "text" : type
  const resolvedRightIcon = !isPasswordInput ? iconRight : undefined
  const hasBuiltInLeftContent = Boolean(iconLeft)
  const hasBuiltInRightContent = (isClearable && hasValue) || shouldRenderPasswordToggle || Boolean(resolvedRightIcon)
  const rightIconsCount =
    Number(isClearable && hasValue) + Number(shouldRenderPasswordToggle || Boolean(resolvedRightIcon))
  const leftContent = renderLeftContent?.({
    disabled,
    readOnly,
    hasValue,
    type,
    effectiveType,
    isPasswordVisible,
    hasBuiltInContent: hasBuiltInLeftContent,
  })
  const rightContent = renderRightContent?.({
    disabled,
    readOnly,
    hasValue,
    type,
    effectiveType,
    isPasswordVisible,
    hasBuiltInContent: hasBuiltInRightContent,
  })

  const handleClear = () => {
    if (!isClearable || disabled || readOnly) {
      return
    }

    onChange({
      target: { value: "" },
      currentTarget: { value: "" },
    } as React.ChangeEvent<HTMLInputElement>)

    ref.current?.focus()
    setIsPasswordVisible(false)
  }

  const handlePasswordVisibilityToggle = () => {
    if (!isPasswordInput || disabled) {
      return
    }

    setIsPasswordVisible((currentValue) => !currentValue)
    ref.current?.focus()
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value

    if (!onChange || type !== "number" || rawValue === "") {
      onChange?.(event)
      return
    }

    const numericValue = event.currentTarget.valueAsNumber

    if (Number.isNaN(numericValue)) {
      onChange(event)
      return
    }

    onChange({
      ...event,
      target: { ...event.target, value: numericValue },
      currentTarget: { ...event.currentTarget, value: numericValue },
    } as unknown as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <div
      aria-invalid={ariaInvalid}
      className={cn(
        "relative overflow-hidden rounded-md border border-input outline-none focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40",
        readOnly && "cursor-default",
        className
      )}
    >
      {(iconLeft || leftContent) && (
        <span
          className={cn(
            "absolute top-1/2 left-2 flex -translate-y-1/2 items-center gap-1 text-muted-foreground",
            !leftContent && "pointer-events-none"
          )}
        >
          {iconLeft && <Icon icon={iconLeft} data-icon="inline-start" size="md" {...iconLeftProps} {...iconProps} />}
          {leftContent}
        </span>
      )}
      <input
        ref={setInputRef}
        type={effectiveType}
        data-slot="input"
        disabled={disabled}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : props.tabIndex}
        onChange={handleChange}
        value={value}
        className={cn(
          "h-8 w-full min-w-0 rounded-none bg-transparent px-2.5 py-1 text-base outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80",
          readOnly && "cursor-default",
          iconLeft && "pl-8",
          rightIconsCount === 1 && "pr-8",
          rightIconsCount === 2 && "pr-12",
          type === "number" &&
            "appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          inputClassName
        )}
        aria-invalid={ariaInvalid}
        {...props}
      />
      {(hasBuiltInRightContent || rightContent) && (
        <span className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1 text-muted-foreground">
          {isClearable && hasValue && (
            <button
              type="button"
              onClick={handleClear}
              className="cursor-pointer rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:text-foreground"
              aria-label={clearLabel}
              disabled={disabled || readOnly}
              tabIndex={-1}
            >
              <Icon icon={X} size="md" data-icon="clear" />
            </button>
          )}
          {shouldRenderPasswordToggle ? (
            <button
              type="button"
              onClick={handlePasswordVisibilityToggle}
              className="cursor-pointer rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:text-foreground"
              aria-label={isPasswordVisible ? "Hide password" : "Show password"}
              disabled={disabled}
              tabIndex={-1}
            >
              <Icon icon={isPasswordVisible ? Eye : EyeOff} data-icon="password-visibility" size="md" />
            </button>
          ) : (
            resolvedRightIcon && (
              <Icon icon={resolvedRightIcon} data-icon="inline-end" size="md" {...iconRightProps} {...iconProps} />
            )
          )}
          {rightContent}
        </span>
      )}
    </div>
  )
}

export { Input }
